import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  MdAdd,
  MdAssignment,
  MdClose,
  MdCloudUpload,
  MdDeleteOutline,
  MdDescription,
  MdLocalShipping,
  MdRefresh,
  MdSearch,
  MdSettings,
} from "react-icons/md";
import NewArticle from "./NewArticle";
import "./NewGoodsReceived.scss";

interface NewGoodsReceivedProps {
  onClose: () => void;
}

function getToday() {
  const today = new Date();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  return `${today.getFullYear()}-${month}-${day}`;
}

export default function NewGoodsReceived({ onClose }: NewGoodsReceivedProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedDocument, setSelectedDocument] = useState<File | null>(null);
  const [deliveryDate] = useState(getToday);
  const [isArticleModalOpen, setIsArticleModalOpen] = useState(false);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    const previousOverflow = document.body.style.overflow;
    dialog.showModal();
    document.body.style.overflow = "hidden";

    return () => {
      dialog.close();
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  return createPortal(
    <>
      <dialog
        ref={dialogRef}
        className="new-goods-received-modal"
        aria-labelledby="new-goods-received-title"
        aria-describedby="new-goods-received-description"
        onCancel={(event) => {
          event.preventDefault();
          onClose();
        }}
        onClick={(event) => {
          if (event.target === event.currentTarget) onClose();
        }}
      >
        <form className="new-goods-received-modal__form" onSubmit={(event) => event.preventDefault()}>
          <button
            type="button"
            className="new-goods-received-modal__close"
            onClick={onClose}
            aria-label="Modal schließen"
            autoFocus
          >
            <MdClose aria-hidden="true" />
          </button>

          <header className="new-goods-received-modal__header">
            <h2 id="new-goods-received-title">Neuen Wareneingang anlegen</h2>
            <p id="new-goods-received-description">
              Erfassen Sie den Lieferschein und die eingegangenen Artikel. Pflichtfelder sind mit * markiert.
            </p>
          </header>

          <div className="new-goods-received-modal__content">
            <div className="new-goods-received-modal__top-grid">
              <section className="new-goods-received-modal__section" aria-labelledby="goods-received-delivery-title">
                <h3 id="goods-received-delivery-title" className="new-goods-received-modal__section-title">
                  <MdLocalShipping aria-hidden="true" /> Lieferschein Informationen
                </h3>
                <div className="new-goods-received-modal__section-content">
                  <div className="new-goods-received-modal__field-row">
                    <div className="new-goods-received-modal__field">
                      <label htmlFor="goods-received-delivery-number">Lieferschein Nr. *</label>
                      <input id="goods-received-delivery-number" name="deliveryNumber" type="text" placeholder="Lieferscheinnummer" required />
                    </div>
                    <div className="new-goods-received-modal__field">
                      <label htmlFor="goods-received-date">Datum *</label>
                      <input id="goods-received-date" name="deliveryDate" type="date" defaultValue={deliveryDate} required />
                    </div>
                  </div>
                  <div className="new-goods-received-modal__distributor-row">
                    <div className="new-goods-received-modal__field">
                      <label htmlFor="goods-received-distributor">Distributor *</label>
                      <select id="goods-received-distributor" name="distributor" defaultValue="" required>
                        <option value="" disabled>Noch keine Distributoren verfügbar</option>
                      </select>
                    </div>
                    <button
                      type="button"
                      className="new-goods-received-modal__icon-btn"
                      aria-label="Distributoren aktualisieren"
                      title="Distributoren werden später angebunden"
                      disabled
                    >
                      <MdRefresh aria-hidden="true" />
                    </button>
                    <button
                      type="button"
                      className="new-goods-received-modal__btn new-goods-received-modal__btn--secondary"
                      title="Die Distributorverwaltung wird später angebunden"
                      disabled
                    >
                      Verwalten
                    </button>
                  </div>
                  <div className="new-goods-received-modal__field">
                    <label htmlFor="goods-received-note">Notiz</label>
                    <textarea id="goods-received-note" name="note" rows={3} placeholder="Notiz zum Lieferschein" />
                  </div>
                </div>
              </section>

              <section className="new-goods-received-modal__section" aria-labelledby="goods-received-document-title">
                <h3 id="goods-received-document-title" className="new-goods-received-modal__section-title">
                  <MdDescription aria-hidden="true" /> Dokument hochladen
                </h3>
                <div className="new-goods-received-modal__section-content">
                  <label className="new-goods-received-modal__dropzone">
                    <input
                      ref={fileInputRef}
                      type="file"
                      name="deliveryDocument"
                      accept="application/pdf,image/*"
                      aria-label="Lieferscheindokument auswählen"
                      aria-describedby="goods-received-upload-hint"
                      onChange={(event) => setSelectedDocument(event.target.files?.[0] ?? null)}
                    />
                    <MdCloudUpload aria-hidden="true" />
                    <span>Datei auswählen</span>
                    <small>PDF oder Bild</small>
                  </label>
                  {selectedDocument && (
                    <div className="new-goods-received-modal__document" aria-live="polite">
                      <MdDescription aria-hidden="true" />
                      <span>{selectedDocument.name}</span>
                      <button
                        type="button"
                        className="new-goods-received-modal__icon-btn"
                        aria-label="Ausgewähltes Dokument entfernen"
                        onClick={() => {
                          setSelectedDocument(null);
                          if (fileInputRef.current) fileInputRef.current.value = "";
                        }}
                      >
                        <MdClose aria-hidden="true" />
                      </button>
                    </div>
                  )}
                  <p id="goods-received-upload-hint" className="new-goods-received-modal__hint">
                    Das Dokument wird nur lokal ausgewählt und noch nicht hochgeladen.
                  </p>
                </div>
              </section>
            </div>

            <section className="new-goods-received-modal__section" aria-labelledby="goods-received-search-title">
              <h3 id="goods-received-search-title" className="new-goods-received-modal__section-title">
                <MdSearch aria-hidden="true" /> Wareneingang Artikelsuche
              </h3>
              <div className="new-goods-received-modal__section-content">
                <div className="new-goods-received-modal__field-row">
                  <div className="new-goods-received-modal__field">
                    <label htmlFor="goods-received-index-search">Indexsuche</label>
                    <input id="goods-received-index-search" name="indexSearch" type="search" placeholder="Artikelname oder Artikelnummer" aria-describedby="goods-received-search-hint" />
                  </div>
                  <div className="new-goods-received-modal__field">
                    <label htmlFor="goods-received-ean">EAN</label>
                    <input id="goods-received-ean" name="ean" type="text" inputMode="numeric" placeholder="EAN eingeben oder scannen" aria-describedby="goods-received-search-hint" />
                  </div>
                </div>
                <div className="new-goods-received-modal__search-actions">
                  <p id="goods-received-search-hint" className="new-goods-received-modal__hint">
                    Die Artikelsuche wird später mit den Stammdaten verbunden.
                  </p>
                  <button
                    type="button"
                    className="new-goods-received-modal__btn new-goods-received-modal__btn--secondary"
                    onClick={() => setIsArticleModalOpen(true)}
                  >
                    <MdAdd aria-hidden="true" /> Neuen Artikel anlegen
                  </button>
                </div>
              </div>
            </section>

            <section className="new-goods-received-modal__section" aria-labelledby="goods-received-items-title">
              <h3 id="goods-received-items-title" className="new-goods-received-modal__section-title">
                <MdAssignment aria-hidden="true" /> Positionen auf Wareneingang – Lieferschein
              </h3>
              <div className="new-goods-received-modal__table-wrapper">
                <table className="new-goods-received-modal__table" aria-labelledby="goods-received-items-title">
                  <thead>
                    <tr>
                      <th scope="col"><input type="checkbox" aria-label="Alle Positionen auswählen" disabled /></th>
                      <th scope="col">Nr.</th>
                      <th scope="col">Beschreibung</th>
                      <th scope="col">Bemerkung</th>
                      <th scope="col">IMEI</th>
                      <th scope="col">Menge</th>
                      <th scope="col">EK</th>
                      <th scope="col">VK</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td colSpan={8} className="new-goods-received-modal__empty-state">
                        Noch keine Artikel zum Wareneingang hinzugefügt.
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>
          </div>

          <footer className="new-goods-received-modal__footer">
            <h3 className="new-goods-received-modal__section-title">
              <MdSettings aria-hidden="true" /> Aktion
            </h3>
            <div className="new-goods-received-modal__footer-actions">
              <button
                type="button"
                className="new-goods-received-modal__btn new-goods-received-modal__btn--secondary"
                title="Keine Positionen zum Löschen vorhanden"
                disabled
              >
                <MdDeleteOutline aria-hidden="true" /> Löschen
              </button>
              <p id="goods-received-booking-hint" className="new-goods-received-modal__hint">
                Die Buchung wird später angebunden.
              </p>
              <button
                type="submit"
                className="new-goods-received-modal__btn new-goods-received-modal__btn--submit"
                aria-describedby="goods-received-booking-hint"
                disabled
              >
                Wareneingang buchen
              </button>
            </div>
          </footer>
        </form>
      </dialog>

      {isArticleModalOpen && <NewArticle onClose={() => setIsArticleModalOpen(false)} />}
    </>,
    document.body,
  );
}
